import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Forms, Loading, SystemText } from 'e4shared';
import Filters from '../_filters';
import CompetencyPicker from 'components/admin/forms/competencyPicker';
import GroupPicker from 'components/admin/forms/groupPicker';
import Board, { BoardMetric } from './board';
import WidgetBase, { groupSet, widgetLoad, widgetLoadRecommendations } from '../_base';

export const CompetencyROI = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});

    const defaults = { show_For: true, period1: 'LM3' };

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;

        setValues(temp);
    };

    const height = defaultProps.height(item);

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);

        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/CompetencyROI/Fields/Group/Error');
            validForm = false;
        }

        if (!form.competency1?.areaId && form.competency1?.areaId !== -1) {
            errors.competency1 = SystemText('Dashboard/Widgets/CompetencyROI/Fields/Competency/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <Board
                    data={data}
                    item={item}
                    id={`chart-${item.id}`}
                    setItem={setItem}
                    reload={x => setRecommendationData(x)}
                />
            </div>
        </div>
    );

    const recommendations = React.useCallback(() => {
        if (recLoading) return <Loading />;
        return !ArrayHelpers.existsAndHasLength(recommendationData) ? (
            <div>{SystemText('Dashboard/Widgets/CompetencyROI/Recommendations/NoMetrics')}</div>
        ) : (
            <>
                <div className="mb10">{SystemText('Dashboard/Widgets/CompetencyROI/Recommendations/Metrics')}</div>
                {recommendationData?.map(m => {
                    return <BoardMetric metric={m} key={`rec-metric-${m.id}`} ext="rec" />;
                })}
            </>
        );
    }, [recommendationData, recLoading]);

    const filters = values ? (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <CompetencyPicker
                label={SystemText('Dashboard/Widgets/CompetencyROI/Fields/Competency/Label')}
                mandatory
                value={values.competency1}
                error={errors.competency1}
                onChange={value => {
                    updateProperty(value, 'competency1');
                }}
            />
            <Forms.Buttons
                label={SystemText('Dashboard/Widgets/CompetencyROI/Fields/Months/Label')}
                help={SystemText('Dashboard/Widgets/CompetencyROI/Fields/Months/Help')}
            >
                <button
                    className={`btn btn-${values.period1 === 'LM1' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('LM1', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/CompetencyROI/Fields/Months/1')}
                </button>
                <button
                    className={`btn btn-${!['LM1', 'LM6', 'LM12'].some(x => x === values.period1) ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('LM3', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/CompetencyROI/Fields/Months/3')}
                </button>
                <button
                    className={`btn btn-${values.period1 === 'LM6' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('LM6', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/CompetencyROI/Fields/Months/6')}
                </button>
                <button
                    className={`btn btn-${values.period1 === 'LM12' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('LM12', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/CompetencyROI/Fields/Months/12')}
                </button>
            </Forms.Buttons>
        </Filters>
    ) : null;

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={item.extra}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default CompetencyROI;
